import globalAwsCredentials from 'lib/globalAwsCredentials';
import Kinesis from 'aws-sdk/clients/kinesis';
import config from 'lib/config';
import Cookies from 'js-cookie';
import SessionStartedEvent from 'lib/Tracking/SessionStartedEvent';
import { v4 as uuid } from 'uuid';
import Store from './Store';
import MemoryStore from './MemoryStore';
import * as LocalStorageStore from './LocalStorageStore';
import AutolistEvent, { EventData } from './Event';
import UserData from './User';

const DEFAULT_KINESIS_CONFIG = { apiVersion: '2013-12-02', endpoint: undefined };

function setClientGuidTimestampCookie(clientGuidTimestamp: string): void {
  Cookies.set('client_guid_timestamp', clientGuidTimestamp);
}

function getLocalStorage(): null | typeof localStorage {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage;
}

function getBestStore(): Store {
  const localstorage = getLocalStorage();

  if (!localstorage) {
    return new MemoryStore();
  }

  try {
    // test to see if localstorage exceeds quota
    const id = uuid();
    localstorage.setItem(id, uuid());
    localstorage.removeItem(id);
  } catch (error) {
    LocalStorageStore.clearAutolistCoreSequenceNumberItems();
    return new MemoryStore();
  }

  return LocalStorageStore;
}

export class EventLogger {
  kinesis: Kinesis;

  store: Store;

  user?: UserData;

  constructor() {
    this.kinesis = new Kinesis({
      credentials: globalAwsCredentials,
      ...DEFAULT_KINESIS_CONFIG,
      endpoint: config.eventsKinesisEndpoint,
      region: 'us-east-1',
    });
    this.store = getBestStore();

    setClientGuidTimestampCookie(this.clientGuidTimestamp);
  }

  get clientGuidTimestamp(): string {
    if (this.clientGuid.startsWith('amp-')) {
      return this.clientGuid;
    }
    return `${this.clientGuid}.${this.clientFirstTouchedTimestamp}`;
  }

  async touchSession(): Promise<void> {
    if (this.store.touchSession()) {
      await this.logSessionStarted();
    }
  }

  async logSessionStarted(): Promise<void> {
    await this.log(new SessionStartedEvent());
  }

  async log(eventData: EventData): Promise<void> {
    const event = new AutolistEvent({
      data: {
        ata_client_id: this.clientGuidTimestamp,
        user_id: this.user?.id,
        ...eventData,
      },
      store: this.store,
    });

    if (config.debugEvents) {
      console.log(`Autolist event logged`, event.toJSON());
    }

    this.store.incrementSequenceNumber();

    await this.postEventToKinesis(event);
  }

  async postEventToKinesis(event: AutolistEvent): Promise<void> {
    if (config.disableEvents) return;

    await this.kinesis.putRecord(this.createRecord(event)).promise();
  }

  createRecord(event: AutolistEvent): Kinesis.PutRecordInput {
    return {
      StreamName: config.eventsKinesisStreamName,
      Data: `${JSON.stringify(event)}\n`,
      PartitionKey: this.store.getSessionGuid(),
    };
  }

  get clientGuid(): string {
    return this.store.getClientGuid();
  }

  get clientFirstTouchedTimestamp(): number {
    return this.store.getClientFirstTouchedTimestamp();
  }
}

const el = new EventLogger();
export default el;
